import React, { useEffect, useRef, useState } from 'react';
import type { DeckRailState } from './types';

/*
 * Beside the deck: what Orbit is reading while the person answers. Counts that
 * move, bars that fill, what it just learned, the departments it has found.
 */

export const DeckRail: React.FC<{ rail: DeckRailState }> = ({ rail }) => {
  const [bumped, setBumped] = useState<string[]>([]);
  const previous = useRef<Record<string, number>>({});

  useEffect(() => {
    const changed = rail.areas.filter((a) => previous.current[a.key] !== undefined && previous.current[a.key] !== a.pct).map((a) => a.key);
    rail.areas.forEach((a) => { previous.current[a.key] = a.pct; });
    if (changed.length === 0) return;
    setBumped(changed);
    const t = window.setTimeout(() => setBumped([]), 1200);
    return () => window.clearTimeout(t);
  }, [rail.areas]);

  return (
    <aside className="odeck-rail">
      <div className="odeck-live"><i />Orbit is reading while you answer</div>
      <div className="odeck-rail-card">
        <h3>{rail.site || 'Waiting for a website or a document'}</h3>
        <small>{rail.site ? 'First read · started when it was given' : 'Type a website or drop a document on any card'}</small>
        {rail.read && (
          <div className="odeck-rail-read">
            <div className="line"><span className="spin" aria-hidden /><b>{rail.read.doing}</b></div>
            <div className="bar"><i style={{ width: `${Math.round((rail.read.done / Math.max(rail.read.total, 1)) * 100)}%` }} /></div>
            <small>{rail.read.done} of {rail.read.total} passes done. The cards it answers leave the deck on their own.</small>
          </div>
        )}
        {rail.pages !== undefined && <div className="odeck-read-row"><span>Pages read</span><b>{rail.pages}</b></div>}
        <div className="odeck-read-row"><span>Facts kept</span><b>{rail.facts}</b></div>
        <div className="odeck-read-row"><span>Cards answered for you</span><b>{rail.auto}</b></div>
        {rail.skipped !== undefined && <div className="odeck-read-row"><span>Skipped</span><b>{rail.skipped}</b></div>}
        {rail.left !== undefined && <div className="odeck-read-row"><span>Not answered yet</span><b>{rail.left}</b></div>}
        {rail.forYou !== undefined && <div className="odeck-read-row"><span>For you</span><b>{rail.forYou}</b></div>}
        <div className="odeck-read-row"><span>Departments found</span><b>{rail.departments ?? rail.doors.length}</b></div>
      </div>
      <div className="odeck-rail-card plain">
        <h3>What Orbit knows so far</h3>
        {rail.areas.map((a) => (
          <div key={a.key} className={`odeck-mod ${bumped.includes(a.key) ? 'bump' : ''}`}>
            <span>{a.label}</span><b className="pct">{a.pct}%</b>
            <i><b style={{ width: `${a.pct}%` }} /></i>
          </div>
        ))}
      </div>
      <div className="odeck-learned">
        <h4>Just learned</h4>
        <ul>
          {rail.learned.slice(0, 4).map((l, i) => <li key={`${l.text}-${i}`} className={l.gold ? 'gold' : ''}><i />{l.text}</li>)}
          {rail.learned.length === 0 && <li><i />Nothing yet. Answer a card or give Orbit something to read.</li>}
        </ul>
      </div>
      {rail.doors.length > 0 && (
        <div className="odeck-doors">
          {rail.doors.map((d) => <div key={d.key} className={`odeck-door ${d.found ? 'found' : ''}`}><span>{d.name.slice(0, 2).toUpperCase()}</span>{d.name}</div>)}
        </div>
      )}
    </aside>
  );
};

export default DeckRail;
